import { computed, ref } from "vue";
import type { NoteConfig } from "@/types/legend.types";
import NoteMenuState from "./NoteMenuState";
import CanvasSettingsState from "./CanvasSettingsState";
import { resolveActiveCatalogue } from "@/utils/catalogue-tabs";

/**
 * Catalogues de la légende active, affichés en onglets dans l'en-tête : leurs
 * notes remplissent la ligne des notes juste dessous.
 *
 * category.noteIds est l'ordre de référence pour chaque catalogue (indépendant
 * d'un catalogue à l'autre) : on résout les ids vers leurs notes plutôt que de
 * filtrer les notes par appartenance, ce qui donnerait l'ordre du tableau
 * global, pas celui du catalogue. Toute note appartient obligatoirement à au
 * moins un catalogue (voir configurateur) — pas de groupe "sans catalogue".
 */
export const catalogues = computed<Map<string, NoteConfig[]>>(() => {
  const map = new Map<string, NoteConfig[]>();
  const active = NoteMenuState.activeCategories;
  const notes = NoteMenuState.noteConfigs.filter((n) => n.type !== "photo");
  const notesById = new Map(notes.map((n) => [n.id, n]));
  const legendCategories = CanvasSettingsState.activeLegend?.categories ?? [];

  for (const category of legendCategories) {
    if (active.length && !active.includes(category.name)) continue;
    const categoryNotes = category.noteIds
      .map((id) => notesById.get(id))
      .filter((n): n is NoteConfig => !!n);
    if (categoryNotes.length > 0) map.set(category.name, categoryNotes);
  }

  return map;
});

/**
 * Onglet demandé. `""` = pas encore choisi (le premier catalogue s'ouvre),
 * `null` = refermé par l'utilisateur (aucun onglet, ligne des notes repliée).
 */
const requestedCatalogue = ref<string | null>("");

export const activeCatalogue = computed<string | null>(() =>
  resolveActiveCatalogue(requestedCatalogue.value, [
    ...catalogues.value.keys(),
  ]),
);

/** Notes du catalogue ouvert (vide quand la ligne est repliée). */
export const activeCatalogueNotes = computed<NoteConfig[]>(() =>
  activeCatalogue.value === null
    ? []
    : (catalogues.value.get(activeCatalogue.value) ?? []),
);

/** Toucher l'onglet ouvert le referme ; toucher un autre onglet l'ouvre. */
export function toggleCatalogue(name: string) {
  requestedCatalogue.value = activeCatalogue.value === name ? null : name;
}

/** À l'ouverture d'un plan : le premier catalogue s'ouvre de nouveau. */
export function resetCatalogueTabs() {
  requestedCatalogue.value = "";
}
